import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { Stack } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import zodLocales from 'common/zod/locales';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import * as Form from 'components/_core/form';
import Heading from 'components/_core/Heading';
import BackButton from 'components/BackButton';
import StepperProgressBar from 'components/Stepper/StepperProgressBar';
import { StepProps } from 'components/Stepper/types';
import * as Layout from 'layouts/StepperLayout';

import { useExportCreationContext } from '../../hooks/useExportCreationContext';
import locales from './locales';

const SelectRecipient: FC<StepProps> = ({ goNext }) => {
  const {
    flowConfig: { user },
    setSharedState,
  } = useExportCreationContext();

  const formSchema = z.object({
    email: z.string().min(1, { message: zodLocales.errors.required }).email({
      message: zodLocales.errors.invalidEmail,
    }),
    name: z.string().min(1, {
      message: zodLocales.errors.required,
    }),
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    register,
  } = useForm({
    defaultValues: {
      email: user.email || '',
      name: `${user.firstName} ${user.lastName}`,
    },
    mode: 'onChange',
    resolver: zodResolver(formSchema),
  });

  const onSubmit = handleSubmit((values) => {
    setSharedState((prevState) => ({
      ...prevState,
      ...values,
    }));

    goNext();
  });

  return (
    <Layout.Root>
      <Layout.Body>
        <Layout.HeaderButtonsContainer>
          <BackButton />
        </Layout.HeaderButtonsContainer>

        <Layout.BodyScrollableSection>
          <Heading marginBottom="space-32" size="lg">
            {locales.title}
          </Heading>

          <Callout marginBottom="space-32">
            <Callout.Title>{locales.warning}</Callout.Title>
          </Callout>

          <Stack spacing="space-16">
            <Form.Field error={errors.name?.message} label={locales.name}>
              <Form.Input {...register('name')} />
            </Form.Field>

            <Form.Field error={errors.email?.message} label={locales.email}>
              <Form.Input {...register('email')} type="email" />
            </Form.Field>
          </Stack>
        </Layout.BodyScrollableSection>

        <Layout.FooterContainer>
          <StepperProgressBar />

          <Layout.FooterButtonsContainer>
            <Button icon="arrow-right" isDisabled={!isValid} onClick={onSubmit}>
              {locales.next}
            </Button>
          </Layout.FooterButtonsContainer>
        </Layout.FooterContainer>
      </Layout.Body>
    </Layout.Root>
  );
};

export default SelectRecipient;
